import React, {useState, useEffect, useRef} from "react"
import InmuebleCard from "@/components/properties/PropertyCard"
import {CSVPopUpWindow} from "@/components/csv/CSVPopUpWindow"
import Page from "@/layouts/Page"
import {getPartnersProperties} from "@/api/properties"
import {getAssociates} from "@/api/asociados"
import {Box} from "@mui/material"
import ListAltIcon from "@mui/icons-material/ListAlt"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import Chip from "@mui/material/Chip"
import OutlinedInput from "@mui/material/OutlinedInput"
import {AxiosResponse} from "axios"
import {CDBLUE} from "@/constants"
import AddIcon from "@mui/icons-material/Add"
import {useAuthContext} from "@/hooks/useAuthContext"
import {Link, useLocation} from "react-router-dom"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import {PropertyResponse as Property} from "@/types/Property"
import {PropertyStatus, condoClasses} from "@casas-diamante/types"
import RegionSelector from "@/components/ui/RegionSelector"
import {useRegion} from "@/context/RegionContext"
import AssociatesSelect from "@/components/properties/AssociatesSelect"
import {
   persistPropertySearchState,
   PropertySearchState,
   readPersistedPropertySearchState,
   usePropertySearchState,
} from "@/hooks/useBackNavigation"
import {parseStoredStringArray, renderSelectedFilterValues} from "@/utils"

interface Associate {
   value: string
   label: string
}

function Inmuebles() {
   const location = useLocation()
   const restoredRef = useRef(false)
   const pendingRestoreRef = useRef<PropertySearchState | null>(null)
   const [inmuebles, setInmuebles] = useState<Property[]>([])
   const [loading, setLoading] = useState(false)
   const [fetchingMore, setFetchingMore] = useState(false)
   const [noMoreInm, setNoMoreInm] = useState(false)
   const [partners, setPartners] = useState<string[]>([])
   const [partnersFetched, setPartnersFetched] = useState<Associate[]>([])
   const [suspended, setSuspended] = useState<string | null>(null)
   const [available, setAvailable] = useState<string | null>(null)
   const [searchString, setSearchString] = useState("")
   const [searchPage, setSearchPage] = useState(0)
   const [totalHits, setTotalHits] = useState(0)
   const [hasSearched, setHasSearched] = useState(false)
   const [visibility, setVisibility] = useState<boolean>(false)
   const [status, setStatus] = useState<string[]>([])
   const [condoClass, setCondoClass] = useState<string[]>([])
   const {user} = useAuthContext()

   const {selectedRegion} = useRegion()

   const storedInitialProperties = JSON.parse(localStorage.getItem("initialProperties") || "[]")

   const storedFilterKey = localStorage.getItem("filterKey") || ""
   const setStoredFilterKey = (key: string) => localStorage.setItem("filterKey", key)

   const setStoredSuspended = (val: string | null) => localStorage.setItem("suspended", val || "")
   const storedSuspended = localStorage.getItem("suspended") || ""

   const setStoredAvailable = (val: string | null) => localStorage.setItem("available", val || "")
   const storedAvailable = localStorage.getItem("available") || ""

   const storedSearchQuery = localStorage.getItem("query") || ""
   const setStoredSearchQuery = (val: string) => localStorage.setItem("query", val)

   const storedPartners: string[] = JSON.parse(localStorage.getItem("partners") || "[]")
   const setStoredPartners = (val: string[]) =>
      localStorage.setItem("partners", JSON.stringify(val))

   const fetchedPartners = JSON.parse(localStorage.getItem("fetchedPartners") || "[]")
   const setFetchedPartners = (val: any[]) =>
      localStorage.setItem("fetchedPartners", JSON.stringify(val))

   const fetchedProperties = JSON.parse(localStorage.getItem("properties") || "[]")
   const setFetchedProperties = (val: any[]) =>
      localStorage.setItem("properties", JSON.stringify(val))

   const storedStatus = parseStoredStringArray(localStorage.getItem("status") || "")
   const setStoredStatus = (val: string[]) => localStorage.setItem("status", JSON.stringify(val))

   const storedCondoClass = parseStoredStringArray(localStorage.getItem("condoClass") || "")
   const setStoredCondoClass = (val: string[]) =>
      localStorage.setItem("condoClass", JSON.stringify(val))

   useEffect(() => {
      const restoredSearch =
         (location.state as {restoredSearch?: PropertySearchState} | null)?.restoredSearch ??
         readPersistedPropertySearchState()

      if (!restoredSearch || restoredRef.current || restoredSearch.source !== "properties") {
         return
      }

      restoredRef.current = true
      setSearchString(restoredSearch.searchString ?? "")
      setPartners(restoredSearch.partners ?? [])
      setSuspended(restoredSearch.suspended ?? null)
      setAvailable(restoredSearch.available ?? null)
      setSearchPage(restoredSearch.searchPage ?? 0)
      setStatus(parseStoredStringArray(restoredSearch.status))
      setCondoClass(parseStoredStringArray(restoredSearch.condoClass))
      setInmuebles(restoredSearch.inmuebles)
      setTotalHits(restoredSearch.totalHits ?? 0)
      setNoMoreInm(restoredSearch.noMoreInm ?? false)
      setHasSearched(restoredSearch.inmuebles.length > 0)
      setFetchedProperties(restoredSearch.inmuebles)
      setStoredSearchQuery(restoredSearch.searchString ?? "")
      setStoredPartners(restoredSearch.partners ?? [])
      setStoredSuspended(restoredSearch.suspended ?? null)
      setStoredAvailable(restoredSearch.available ?? null)
      setStoredStatus(parseStoredStringArray(restoredSearch.status))
      setStoredCondoClass(parseStoredStringArray(restoredSearch.condoClass))
      pendingRestoreRef.current = restoredSearch
   }, [location.state])

   useEffect(() => {
      const restoredSearch = pendingRestoreRef.current

      if (!restoredSearch || inmuebles.length === 0) return

      const restorePosition = () => {
         const selectedCard = restoredSearch.selectedPropertyId
            ? document.querySelector(`[data-property-id="${restoredSearch.selectedPropertyId}"]`)
            : null

         if (selectedCard instanceof HTMLElement) {
            selectedCard.scrollIntoView({behavior: "auto", block: "center"})
         } else {
            window.scrollTo({top: restoredSearch.scrollY, behavior: "auto"})
         }

         pendingRestoreRef.current = null
      }

      const timeoutId = window.setTimeout(() => {
         requestAnimationFrame(restorePosition)
      }, 0)

      return () => {
         window.clearTimeout(timeoutId)
      }
   }, [inmuebles.length])

   // Fetch partners when the component mounts
   useEffect(() => {
      const fetchPartners = async () => {
         if (fetchedPartners?.length > 0) {
            setPartnersFetched(fetchedPartners)
         }
         const res = await getAssociates()
         const associates: Associate[] = res.map((associate: any) => ({
            value: associate.id,
            label: associate.name,
         }))
         setPartnersFetched(associates)
         setFetchedPartners(associates)
      }

      const dataCache = async () => {
         if (restoredRef.current) return
         if (storedSearchQuery) setSearchString(storedSearchQuery)
         if (storedPartners?.length > 0) setPartners(storedPartners)
         if (storedSuspended) setSuspended(storedSuspended)
         if (storedAvailable) setAvailable(storedAvailable)
         if (storedStatus.length > 0) setStatus(storedStatus)
         if (storedCondoClass.length > 0) setCondoClass(storedCondoClass)
         if (fetchedProperties?.length > 0) {
            setInmuebles(fetchedProperties)
            setSearchPage(searchPage + 1)
            setFetchedProperties(fetchedProperties)
         }
      }
      fetchPartners().then(() => dataCache())
   }, [])

   // Helper function to append more properties to the existing list
   const changeInmuebles = async (res: AxiosResponse<any, any>) => {
      const newData = inmuebles.concat(res.data)
      if (searchString) {
         newData.sort((a: any, b: any) => a.name.localeCompare(b.name))
      }
      setInmuebles(newData)
      if (res.data.length === 0) {
         setNoMoreInm(true)
      }
      setLoading(false)
   }

   const propertySearchState = usePropertySearchState({
      source: "properties",
      returnTo: "/inmuebles",
      inmuebles,
      searchString,
      partners,
      suspended,
      available,
      searchPage,
      totalHits,
      noMoreInm,
      status,
      condoClass,
   })

   useEffect(() => {
      if (!hasSearched && inmuebles.length === 0) return

      persistPropertySearchState({
         ...propertySearchState,
         scrollY: window.scrollY,
      })
   }, [propertySearchState, hasSearched, inmuebles.length])

   useEffect(() => {
      if (!hasSearched && inmuebles.length === 0) return

      const saveScrollPosition = () => {
         persistPropertySearchState({
            ...propertySearchState,
            scrollY: window.scrollY,
         })
      }

      window.addEventListener("scroll", saveScrollPosition, {passive: true})

      return () => {
         window.removeEventListener("scroll", saveScrollPosition)
      }
   }, [propertySearchState, hasSearched, inmuebles.length])

   // Handler for search string changes
   const handleSearchStringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchString(e.target.value)
      setStoredSearchQuery(e.target.value)
   }

   // Unified search handler
   const handleSearch = async (e?: React.FormEvent) => {
      e?.preventDefault()
      setLoading(true)
      setNoMoreInm(false)
      setSearchPage(0)

      const currentFilterKey = JSON.stringify({
         selectedRegion,
         searchString,
         partners,
         suspended,
         available,
         status,
         condoClass,
      })

      // Si el filtro no ha cambiado, usa caché
      if (fetchedProperties?.length > 0 && storedFilterKey === currentFilterKey) {
         setInmuebles(fetchedProperties)
         setLoading(false)
         return
      }

      const initialPage = searchPage === 0 ? 1 : searchPage // Asegura que la página inicial sea 1
      setSearchPage(initialPage)
      try {
         // Si hay un filtro de búsqueda, reinicia la página de búsqueda
         const res = await getPartnersProperties(
            selectedRegion,
            searchString,
            partners,
            suspended || "",
            available || "",
            status,
            initialPage,
            condoClass,
         )

         setSearchPage(initialPage)

         // await changeState(res.data)
         setTotalHits(res.data.totalCount)
         setInmuebles(res.data.data)
         setFetchedProperties(res.data.data)
         setNoMoreInm(res.data.data.length === 0)
         setStoredFilterKey(currentFilterKey) // actualiza el filtro guardado
         setHasSearched(true)
         setLoading(false)
      } catch (error) {
         console.error("Failed to fetch properties:", error)
         setLoading(false)
      }
   }

   // Handler for clearing all search parameters
   const clearSearchParams = () => {
      setPartners([])
      setStoredPartners([])
      setSearchPage(0)
      setSuspended(null)
      setStoredSuspended(null)
      setAvailable(null)
      setStoredAvailable(null)
      setSearchString("")
      setStoredSearchQuery("")
      setTotalHits(0)
      setNoMoreInm(false)
      setStatus([])
      setStoredStatus([])
      setCondoClass([])
      setStoredCondoClass([])
      // Reset to initial properties
      setInmuebles(storedInitialProperties)
      setFetchedProperties(storedInitialProperties)
   }

   const handleFilterChange =
      (key: "suspended" | "available") => (event: SelectChangeEvent<any>) => {
         const value = event.target.value

         if (key === "suspended") {
            const newValue = value === suspended ? null : value
            setSuspended(newValue)
            setStoredSuspended(newValue)
         } else if (key === "available") {
            const newValue = value === available ? null : value
            setAvailable(newValue)
            setStoredAvailable(newValue)
         }
      }

   const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const newValue = typeof value === "string" ? value.split(",") : value
      setStatus(newValue)
      setStoredStatus(newValue)
   }

   const handleCondoClassChange = (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const newValue = typeof value === "string" ? value.split(",") : value
      setCondoClass(newValue)
      setStoredCondoClass(newValue)
   }

   const handleChangePartners = (event: SelectChangeEvent<(string | number)[]>) => {
      const {
         target: {value},
      } = event
      setPartners(typeof value === "string" ? value.split(",") : value.map(String))
   }

   // Handler for fetching more properties
   const fetchMore = async () => {
      setFetchingMore(true)
      setSearchPage(searchPage + 1)

      const res = await getPartnersProperties(
         selectedRegion,
         searchString,
         partners,
         suspended || "",
         available || "",
         status,
         searchPage + 1,
         condoClass,
      )

      await changeInmuebles(res.data)
      setFetchedProperties(inmuebles.concat(res.data))
      setFetchingMore(false)
   }

   return (
      <Page className="flex flex-col items-center my-8 gap-4 p-4 justify-center">
         <h1 className="text-2xl md:text-3xl font-bold text-blue text-center">
            Buscador de Propiedades Socios
         </h1>
         <section className="flex flex-col md:flex-row gap-4 md:gap-8">
            {user.admin && (
               <>
                  <Link to="nuevo" className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white">
                     <AddIcon />
                     Agregar Inmueble
                  </Link>
                  <button
                     onClick={() => setVisibility(true)}
                     className="flex py-2 px-4 gap-2 rounded-full bg-blue text-white"
                  >
                     Ver modo Excel
                     <ListAltIcon />
                  </button>
               </>
            )}
         </section>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-1 w-auto justify-center items-center">
            <RegionSelector />
            {user.admin && (
               <AssociatesSelect
                  value={partners}
                  onChange={handleChangePartners}
                  options={partnersFetched}
               />
            )}
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Disponibilidad</InputLabel>
               <Select
                  value={available || ""}
                  onChange={handleFilterChange("available")}
                  input={<OutlinedInput label="Disponibilidad" />}
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected && (
                           <Chip
                              key={selected}
                              label={selected === "true" ? "Disponible" : "No Disponible"}
                              sx={{bgcolor: CDBLUE, color: "white"}}
                           />
                        )}
                     </Box>
                  )}
               >
                  <MenuItem
                     value="true"
                     onClick={() => setAvailable((prev) => (prev === "true" ? null : "true"))}
                     selected={available === "true"}
                  >
                     Disponible
                  </MenuItem>
                  <MenuItem
                     value="false"
                     onClick={() => setAvailable((prev) => (prev === "true" ? null : "true"))}
                     selected={available === "false"}
                  >
                     No Disponible
                  </MenuItem>
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Visibilidad</InputLabel>
               <Select
                  value={suspended || ""}
                  onChange={handleFilterChange("suspended")}
                  input={<OutlinedInput label="Visibilidad" />}
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected && (
                           <Chip
                              key={selected}
                              label={selected === "true" ? "Apagado" : "Encendido"}
                              sx={{bgcolor: CDBLUE, color: "white"}}
                           />
                        )}
                     </Box>
                  )}
               >
                  <MenuItem
                     value="false"
                     onClick={() => setSuspended((prev) => (prev === "false" ? null : "false"))}
                     selected={suspended === "false"}
                  >
                     Encendido
                  </MenuItem>
                  <MenuItem
                     value="true"
                     onClick={() => setSuspended((prev) => (prev === "true" ? null : "true"))}
                     selected={suspended === "true"}
                  >
                     Apagado
                  </MenuItem>
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Estatus del inmueble</InputLabel>
               <Select
                  multiple
                  value={status}
                  onChange={handleStatusChange}
                  input={<OutlinedInput label="Clasificación" />}
                  renderValue={renderSelectedFilterValues}
               >
                  {Object.values(PropertyStatus).map((s) => (
                     <MenuItem key={s} value={s} selected={status.includes(s)}>
                        {s}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <FormControl className="w-[250px]" color="primary" size="small">
               <InputLabel>Clasificación del condominio</InputLabel>
               <Select
                  multiple
                  value={condoClass}
                  onChange={handleCondoClassChange}
                  input={<OutlinedInput label="Clasificación del condominio" />}
                  renderValue={renderSelectedFilterValues}
               >
                  {condoClasses.map((c) => (
                     <MenuItem key={c} value={c} selected={condoClass.includes(c)}>
                        {c}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
         </div>
         <form className="flex flex-col items-center content-center gap-4" action="submit">
            <div className="flex border border-blue rounded-md w-full px-2 py-1 items-center">
               <input
                  type="text"
                  name="Search String"
                  id="search-string"
                  value={searchString}
                  onChange={handleSearchStringChange}
                  className="px-2 py-1 focus:outline-none"
                  placeholder="Buscar por nombre"
               />
            </div>
            <div className="flex items-center gap-2">
               <button
                  onClick={handleSearch}
                  type="submit"
                  className="border rounded-[10px] bg-blue text-white px-2 py-1"
               >
                  Buscar
               </button>
               <button
                  onClick={clearSearchParams}
                  type="button"
                  className="border rounded-[10px] bg-red text-white px-2 py-1"
               >
                  Restablecer búsqueda
               </button>
            </div>
         </form>
         <div>
            {/* Intial text to start searching property */}
            {!loading && inmuebles.length === 0 && (
               <h2 className="text-center text-xl font-bold mt-20">
                  {hasSearched && totalHits !== 0
                     ? "No se encontraron resultados"
                     : "Busca una propiedad"}
               </h2>
            )}
         </div>
         {loading ? (
            <h2 className="text-center text-xl font-bold mt-20 animate-pulse">Cargando...</h2>
         ) : (
            <div>
               {!loading && totalHits > 0 && (
                  <h4 className="text-center font-bold my-4">
                     {totalHits} resultado{totalHits > 1 && "s"} para la búsqueda.
                  </h4>
               )}
               <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 items-center justify-center gap-8 mx-auto">
                  {inmuebles.map((inmueble) => (
                     <InmuebleCard
                        key={inmueble.id}
                        property={inmueble}
                        searchState={propertySearchState}
                     />
                  ))}
               </section>
            </div>
         )}
         <section className="my-8 flex flex-col gap-4">
            {fetchingMore ? (
               <h2 className="text-center text-xl font-bold mt-20 animate-pulse">
                  Cargando más...
               </h2>
            ) : (
               !noMoreInm &&
               (inmuebles.length % 20 === 0 || (searchString && inmuebles.length % 40 === 0)) && (
                  <button
                     onClick={fetchMore}
                     className="border rounded-[10px] bg-green text-white px-2 py-1"
                  >
                     Cargar más
                  </button>
               )
            )}
            <button
               onClick={() => window.scrollTo({top: 0, behavior: "smooth"})}
               className="border rounded-[10px] bg-green text-white px-2 py-1 flex items-center gap-2"
            >
               Volver arriba
               <ArrowUpwardIcon />
            </button>
         </section>
         {visibility && <CSVPopUpWindow setVisibility={setVisibility} />}
      </Page>
   )
}

export default Inmuebles
